import { dataNodeUtils } from '@/components';
import { ItemMenuCategoryDto } from '@/core';
import {
  CategoryLookupState,
  CategoryState,
  ItemMenuItemDetailsState,
  ItemState,
  useGetCategoriesQuery,
} from '@/redux';
import { array } from '@/utils';
import { Input } from 'antd';
import { useField } from 'formik';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  convertCategoryAndItemsToTreeData,
  ITEM_DESCRIPTION_PREFIX,
} from './convertCategoryAndItemsToTreeData';
import {
  convertItemMenuItemsToTreeTableData,
  ItemMenuTreeTableNode,
} from './convertItemMenuItemsToTreeTableData';
import { MenuItemFormValue } from './FormItemMenuItemSuboptions';
import { Suboptions } from './ItemMenuDetailsForm';
import { ItemMenuTableTree } from './ItemMenuTableTree';
import { extractItemMenuCategories } from './useTreeTableData';

interface Props {
  name: string;
  categoriesName: string;
  communityId: string;
  className?: string;
  initialItems?: ItemMenuItemDetailsState[];
  items: ItemState[];
  initialItemMenuCategories?: ItemMenuCategoryDto[];
}

const ITEM_PREFIX = 'item://';
const CATEGORY_PREFIX = 'category://';

function useTreeData(props: Props, searchString: string) {
  const {
    communityId,
    initialItems = array.empty<ItemMenuItemDetailsState>(),
    initialItemMenuCategories = array.empty<ItemMenuCategoryDto>(),
    items = array.empty<ItemState>(),
  } = props;

  const { data: categories = array.empty<CategoryState>() } =
    useGetCategoriesQuery({ communityId, listed: true });

  const treeData = useMemo(() => {
    const tree = convertCategoryAndItemsToTreeData({
      items,
      categories,
      mapItem,
      mapCategory,
      mapItemDescription,
    });

    const initialTree = convertItemMenuItemsToTreeTableData(
      initialItems!,
      initialItemMenuCategories,
      categories,
      {
        mapItem,
        mapCategory,
        mapItemDescription,
      },
    );

    function sortNodes(
      nodes: ItemMenuTreeTableNode[],
    ): ItemMenuTreeTableNode[] {
      return nodes
        .map((node) => ({
          ...node,
          children: node.children ? sortNodes(node.children) : [],
        }))
        .sort((a, b) => {
          if (a.item && !b.item) return -1;
          if (!a.item && b.item) return 1;

          if (a.category !== b.category)
            return a.category?.name?.localeCompare(b.category?.name ?? '') ?? 0;
          if (a.subCategory1 !== b.subCategory1)
            return (
              a.subCategory1?.name?.localeCompare(b.subCategory1?.name ?? '') ??
              0
            );
          if (a.subCategory2 !== b.subCategory2)
            return (
              a.subCategory2?.name.localeCompare(b.subCategory2?.name ?? '') ??
              0
            );

          return 0;
        });
    }

    const result = dataNodeUtils.merge(
      tree,
      initialTree,
    ) as ItemMenuTreeTableNode[];
    return sortNodes(result);
  }, [items, categories, initialItems, initialItemMenuCategories]);

  const filteredTreeData = useMemo(
    () =>
      dataNodeUtils.filterWithProps(
        treeData,
        searchString,
      ) as ItemMenuTreeTableNode[],
    [treeData, searchString],
  );

  const { categoriesRecord, expandedKeys } = useMemo(() => {
    const itemMenuCategories = extractItemMenuCategories(filteredTreeData);
    const categoriesRecord = itemMenuCategories.reduce<
      Record<string, ItemMenuCategoryDto>
    >((acc, x) => {
      acc[x.categoryId] = x;
      return acc;
    }, {});

    const expandedKeys = itemMenuCategories.map(
      (x) => CATEGORY_PREFIX + x.categoryId,
    );

    return { categoriesRecord, expandedKeys };
  }, [filteredTreeData]);

  return useMemo(
    () => ({
      treeData: filteredTreeData,
      expandedRowKeys: expandedKeys,
      categoriesRecord,
    }),
    [filteredTreeData, expandedKeys, categoriesRecord],
  );
}

const mapItem = (item: ItemState | ItemMenuItemDetailsState) => {
  return {
    key: ITEM_PREFIX + item.id,
    title: item.name,
    id: item.id,
    item: item.name,
  };
};

const mapItemDescription = (item: ItemState | ItemMenuItemDetailsState) => {
  return {
    id: ITEM_DESCRIPTION_PREFIX + item.id,
    key: ITEM_DESCRIPTION_PREFIX + item.id,
    itemDescription: true,
    item: <Suboptions selectedId={item.id} />,
  };
};

const mapCategory = (category: CategoryState | CategoryLookupState) => {
  return {
    key: CATEGORY_PREFIX + category.id,
    title: category.name,
    id: category.id,
  };
};

export function useHandleCheck(props: Props, items: ItemState[]) {
  const { name, initialItemMenuCategories } = props;
  const [{ value }, , { setValue }] = useField<MenuItemFormValue[]>(name);

  const explicitCategoryKeys = (initialItemMenuCategories ?? [])
    .filter((x) => x.explicit === true)
    .map((category) => CATEGORY_PREFIX + category.categoryId);

  const itemKeys = value.map((item) => ITEM_PREFIX + item.itemId);

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([
    ...itemKeys,
    ...explicitCategoryKeys,
  ]);

  const submit = useCallback(
    (checked: string[]) => {
      setSelectedRowKeys(checked);

      const checkedItemIds = checked
        .filter((x) => x.startsWith(ITEM_PREFIX))
        .map((x) => x.substring(ITEM_PREFIX.length));

      const values = value.filter((x) => checkedItemIds.includes(x.itemId));
      const existingItemIds = values.map((x) => x.itemId);
      const newItemIds = checkedItemIds.filter(
        (x) => !existingItemIds.includes(x),
      );

      const newItemValues: MenuItemFormValue[] = newItemIds.map((id) => ({
        itemId: id,
        suboptions: { all: true, values: undefined },
        type: 'Upgrade',
        pristine: false,
        noSuboptions: null,
        standardCredit: false,
        categoryId: items.find((x) => x.id === id)!.categoryId,
      }));

      setValue([...values, ...newItemValues], true);
    },
    [setValue, value, items],
  );

  return useMemo(
    () => ({
      selectedItems: value,
      submit,
      selectedRowKeys,
      setSelectedRowKeys,
    }),
    [value, submit, selectedRowKeys],
  );
}

export function useSelectedCategoriesKeys(
  props: Props,
  selectedRowKeys: string[],
  categoriesRecord: Record<string, ItemMenuCategoryDto>,
  selectedItems: MenuItemFormValue[],
) {
  const { categoriesName } = props;

  const [, , { setValue: setCategories }] =
    useField<Record<string, ItemMenuCategoryDto>>(categoriesName);

  const [explicitCategoryKeys, setExplicitCategoryKeys] = useState<string[]>(
    [],
  );
  const [implicitCategoryKeys, setImplicitCategoryKeys] = useState<string[]>(
    [],
  );

  useEffect(() => {
    const explicitCategories = Object.values(categoriesRecord).filter((x) =>
      selectedRowKeys.some((key) => key.includes(x.categoryId)),
    );

    const explicitKeys = explicitCategories.map(
      (cat) => CATEGORY_PREFIX + cat.categoryId,
    );

    setExplicitCategoryKeys(explicitKeys);

    const itemMenuItemsCategoryIds = selectedItems
      .map((item) => item.categoryId)
      .filter((id): id is string => Boolean(id));
    const implicitItemCategoryIds = itemMenuItemsCategoryIds.map(
      (x) => CATEGORY_PREFIX + x,
    );

    const implicitItemCategories = Object.values(categoriesRecord).filter((x) =>
      implicitItemCategoryIds.some((key) => key.includes(x.categoryId)),
    );

    const implicitKeys = [
      ...explicitCategories.map((x) => CATEGORY_PREFIX + x.categoryId),
      ...explicitCategories
        .filter((x) => x.rootId)
        .map((x) => CATEGORY_PREFIX + x.rootId),
      ...explicitCategories
        .filter((x) => x.parentId)
        .map((x) => CATEGORY_PREFIX + x.parentId),
    ];
    const implicitItemCategoriesKeys = [
      ...implicitItemCategories.map((x) => CATEGORY_PREFIX + x.categoryId),
      ...implicitItemCategories
        .filter((x) => x.rootId)
        .map((x) => CATEGORY_PREFIX + x.rootId),
      ...implicitItemCategories
        .filter((x) => x.parentId)
        .map((x) => CATEGORY_PREFIX + x.parentId),
    ];

    const implicitCategories = Object.values(categoriesRecord).filter((x) =>
      [...implicitKeys, ...implicitItemCategoriesKeys].some((key) =>
        key.includes(x.categoryId),
      ),
    );

    setImplicitCategoryKeys(
      implicitCategories.map((cat) => CATEGORY_PREFIX + cat.categoryId),
    );

    const explicitCategoryRecord = explicitCategories.reduce<
      Record<string, ItemMenuCategoryDto>
    >((acc, category) => {
      acc[category.categoryId] = { ...category, explicit: true };
      return acc;
    }, {});

    const implicitCategoryRecord = implicitCategories.reduce<
      Record<string, ItemMenuCategoryDto>
    >((acc, category) => {
      acc[category.categoryId] = { ...category, explicit: false };
      return acc;
    }, {});

    const mergedCategories: Record<string, ItemMenuCategoryDto> = {
      ...implicitCategoryRecord,
      ...explicitCategoryRecord,
    };

    setCategories(mergedCategories);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowKeys, selectedItems]);

  return useMemo(() => {
    return {
      explicitCategoryKeys,
      implicitCategoryKeys,
      setExplicitCategoryKeys,
    };
  }, [explicitCategoryKeys, implicitCategoryKeys]);
}

export function FormItemMenuItems(props: Props) {
  const { className, items } = props;
  const [searchString, setSearchString] = useState('');
  const { treeData, expandedRowKeys, categoriesRecord } = useTreeData(
    props,
    searchString,
  );

  const {
    submit: handleCheck,
    selectedRowKeys,
    setSelectedRowKeys,
    selectedItems,
  } = useHandleCheck(props, items);
  const {
    implicitCategoryKeys,
    explicitCategoryKeys,
    setExplicitCategoryKeys,
  } = useSelectedCategoriesKeys(
    props,
    selectedRowKeys,
    categoriesRecord,
    selectedItems,
  );

  return (
    <div>
      <Input
        className={className}
        placeholder={t('itemMenus.details.search')}
        value={searchString}
        onChange={(e) => setSearchString(e.target.value)}
      />
      <ItemMenuTableTree
        mode={'edit'}
        expandedRowKeys={expandedRowKeys}
        dataSource={treeData}
        selectedRowKeys={selectedRowKeys}
        onChange={handleCheck}
        selectedCategoriesKeys={[
          ...implicitCategoryKeys,
          ...explicitCategoryKeys,
        ]}
        setSelectedCategories={setExplicitCategoryKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />
    </div>
  );
}
